import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, AudioLines, BookOpen, Boxes, Check, ChevronsUpDown, CircleUserRound, CodeXml, Gauge, House,
  LayoutGrid, Link2, LogOut, Menu, MessagesSquare, PanelLeftClose, PanelLeftOpen, PencilLine, Settings, Sparkles, X,
} from 'lucide-react';
import sidebarMark from 'src/assets/branding/auto-appy-mark-256x256.png';
import { AuthContext } from 'src/context/AuthContext';
import { autoAppyAppInitials, autoAppyAppLaunchUrl, autoAppySwitcherApps } from 'src/config/autoAppyApps';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import { managedOrganisations, resolveTenant } from 'src/features/AiWorkspace/workspace';
import { FOOTER_ITEMS, NAV_GROUPS, activeItemId, destination, isAiProduct, productMark, productTone } from './aiNav';
import { Monogram } from './StageUi';
import './stage.css';
import './ai-stage.css';
import './ai-controls.css';
// Shared widgets used by many lazily loaded pages load once, in a fixed order.
import 'src/common/markdown.css';
import 'src/features/Chat/modelMenu.css';

const ICONS = {
  overview: House, chat: Sparkles, voice: AudioLines, activity: MessagesSquare, studio: Boxes, connections: Link2,
  developers: CodeXml, usage: Gauge, settings: Settings, guide: BookOpen,
};
const RAIL_KEY = 'autoappy.sidebarRail';
const CURRENT_APP = process.env.REACT_APP_CURRENT_APPLICATION || 'auto-appy-ai';
// Pages that belong to one saved bot or channel go back to the library when the organisation changes.
const ORGANISATION_RECORD = /^\/(sales-bots|memory-bots|bots|automation|chat|assistants)(\/|$)/;

const readRail = () => {
  try { return window.localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
};

// Popovers open beside the sidebar (or above the button on phones) and close on Escape or outside clicks.
function usePopover(width = 300) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const toggle = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      const left = Math.min(rect.right + 10, window.innerWidth - width - 12);
      const top = rect.top < window.innerHeight / 2 ? Math.max(12, rect.top) : null;
      setPosition(top === null ? { left: Math.max(12, left), bottom: Math.max(12, window.innerHeight - rect.bottom) } : { left: Math.max(12, left), top });
    }
    setOpen((value) => !value);
  }, [width]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); anchorRef.current?.focus(); } };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return { open, setOpen, toggle, position, anchorRef, panelRef };
}

function NavLink({ item, active, workspaceId, rail, onNavigate }) {
  const Icon = ICONS[item.icon] || House;
  return (
    <Link
      to={destination(item, workspaceId)}
      className={`ws-sb-item${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      title={rail ? item.label : undefined}
      aria-label={rail ? item.label : undefined}
      onClick={onNavigate}
    >
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
      <span className="ws-sb-text">{item.label}</span>
    </Link>
  );
}

export default function StageSidebar({ mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const [rail, setRail] = useState(readRail);
  const products = usePopover(300);
  const account = usePopover(260);
  const organisation = usePopover(300);

  const organisations = managedOrganisations(currentUser);
  const tenant = resolveTenant(currentUser, new URLSearchParams(location.search).get('workspace'));
  const current = organisations.find((item) => item.id === tenant);
  const activeId = activeItemId(location.pathname, location.search);
  const apps = autoAppySwitcherApps(currentUser?.apps);
  const name = currentUser?.name || currentUser?.username || 'Your account';
  const showRail = rail && !mobileOpen;

  useEffect(() => {
    try { window.localStorage.setItem(RAIL_KEY, rail ? '1' : '0'); } catch { /* private mode */ }
  }, [rail]);

  const closeMobile = () => setMobileOpen(false);
  const chooseOrganisation = (id) => {
    organisation.setOpen(false);
    closeMobile();
    if (id === tenant) return;
    if (ORGANISATION_RECORD.test(location.pathname)) {
      // A chat or assistant belongs to one organisation: open the same area in the new one.
      const home = location.pathname.startsWith('/chat') ? '/chat?'
        : location.pathname.startsWith('/assistants') ? '/bot-library?view=assistants&' : '/bot-library?';
      navigate(`${home}workspace=${encodeURIComponent(id)}`);
      return;
    }
    const next = new URLSearchParams(location.search);
    ['page', 'bot', 'channel', 'source'].forEach((key) => next.delete(key));
    next.set('workspace', id);
    navigate(`${location.pathname}?${next}`);
  };
  const signOut = async () => {
    account.setOpen(false);
    try { await logout?.(); } finally { navigate('/'); }
  };

  return (
    <>
      <button type="button" className="ws-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen} aria-controls="ai-sidebar">
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {mobileOpen && <div className="ws-scrim" onClick={closeMobile} aria-hidden="true" />}
      <div id="ai-sidebar" className={`ws-sidebar-shell${mobileOpen ? ' is-open' : ''}`}>
        <nav className={`ws-sidebar${showRail ? ' is-rail' : ''}`} aria-label="Main navigation">
          <div className="ws-sb-top">
            <img src={sidebarMark} alt="" />
            <span className="ws-sb-brand">Auto <span>Appy</span></span>
            <span className="ai-sb-product">AI</span>
            <button type="button" className="ws-sb-iconbtn" onClick={() => setRail((value) => !value)} aria-label={showRail ? 'Expand sidebar' : 'Collapse sidebar'} title={showRail ? 'Expand sidebar' : 'Collapse sidebar'}>
              {showRail ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>

          <button
            type="button"
            ref={organisation.anchorRef}
            className="ws-sb-org"
            title={showRail ? (current?.name || 'Organisation') : undefined}
            aria-label={`Organisation: ${current?.name || 'none chosen'}. Switch organisation`}
            aria-haspopup="dialog"
            aria-expanded={organisation.open}
            onClick={organisation.toggle}
          >
            <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(current?.name || 'Organisation').slice(0, 1)}</span>
            <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
              <span className="ws-sb-org-name" style={{ display: 'block' }}>{current?.name || 'Choose organisation'}</span>
              <span className="ws-sb-org-sub">{current ? `Organisation · ${current.role === 'owner' ? 'Owner' : 'Admin'}` : `${organisations.length} available`}</span>
            </span>
            <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
          </button>

          <div className="ws-sb-scroll">
            {NAV_GROUPS.map((group) => (
              <div className="ws-sb-group" key={group.id} role="group" aria-label={group.label || 'Workspace'}>
                {group.label && <div className="ws-sb-label">{group.label}</div>}
                {group.items.map((item) => (
                  <NavLink key={item.id} item={item} active={activeId === item.id} workspaceId={tenant} rail={showRail} onNavigate={closeMobile} />
                ))}
              </div>
            ))}
          </div>

          <div className="ws-sb-footer">
            {FOOTER_ITEMS.map((item) => (
              <NavLink key={item.id} item={item} active={activeId === item.id} workspaceId={tenant} rail={showRail} onNavigate={closeMobile} />
            ))}
            <button type="button" ref={products.anchorRef} className="ws-sb-item" onClick={products.toggle} aria-haspopup="dialog" aria-expanded={products.open} title={showRail ? 'Products' : undefined} aria-label={showRail ? 'Products' : undefined}>
              <span className="ws-sb-dots" aria-hidden="true"><i style={{ background: '#147DB5' }} /><i style={{ background: '#7CC4E8' }} /><i style={{ background: '#1F9D57' }} /><i style={{ background: '#183F5A' }} /></span>
              <span className="ws-sb-text">Products</span>
              <ArrowUpRight className="ws-sb-meta" size={14} aria-hidden="true" />
            </button>
            <button type="button" ref={account.anchorRef} className="ws-sb-me" onClick={account.toggle} aria-haspopup="menu" aria-expanded={account.open} aria-label={`Account: ${name}`}>
              <Monogram name={name} size={32} />
              <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
                <span className="ws-sb-org-name" style={{ display: 'block' }}>{name}</span>
                <span className="ws-sb-org-sub" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email || 'Profile and sign out'}</span>
              </span>
              <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </div>

      {organisation.open && organisation.position && (
        <div ref={organisation.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch organisation" style={organisation.position}>
          <div className="ws-popover-title">Switch organisation</div>
          {organisations.length === 0 && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>Bots, channels and history belong to an organisation you own or manage. Ask an owner to make you an admin.</p>}
          {organisations.map((item) => (
            <button key={item.id} type="button" className="ws-menu-item" onClick={() => chooseOrganisation(item.id)} aria-current={item.id === tenant ? 'true' : undefined}>
              <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(item.name).slice(0, 1)}</span>
              <span className="ws-grow" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
              <span className="ws-chip">{item.role === 'owner' ? 'Owner' : 'Admin'}</span>
              {item.id === tenant && <Check size={15} color="#147DB5" aria-label="Current organisation" />}
            </button>
          ))}
          <div className="ws-menu-sep" />
          <a className="ws-menu-item" href={autoAppyUrl('/organisation')} style={{ color: '#0F6392', fontWeight: 500 }}>
            <ArrowUpRight size={16} aria-hidden="true" />Manage organisations in Auto Appy
          </a>
        </div>
      )}

      {products.open && products.position && (
        <div ref={products.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch product" style={products.position}>
          <div className="ws-popover-title">Switch product</div>
          {apps.length === 0 && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>No other products are enabled for this account.</p>}
          {apps.map((app) => {
            const here = app.key === CURRENT_APP || (!apps.some((item) => item.key === CURRENT_APP) && isAiProduct(app));
            const label = app.key === 'main' ? 'Auto Appy' : app.name;
            const body = (
              <>
                <span className="ws-product-icon" style={{ background: productTone(app), fontSize: productMark(app).length > 2 ? 9.5 : 11 }}>{productMark(app)}</span>
                <span className="ws-grow">{label}</span>
                {here ? <span className="ws-chip">Current</span> : <ArrowUpRight size={15} aria-hidden="true" />}
              </>
            );
            return here
              ? <Link key={app.key} className="ws-menu-item" to={tenant ? `/dashboard?workspace=${encodeURIComponent(tenant)}` : '/dashboard'} onClick={() => { products.setOpen(false); closeMobile(); }} aria-current="page">{body}</Link>
              : <a key={app.key} className="ws-menu-item" href={autoAppyAppLaunchUrl(app, tenant)} onClick={() => products.setOpen(false)}>{body}</a>;
          })}
          <a className="ws-menu-item" href={autoAppyUrl('/autoappy/apps')} onClick={() => products.setOpen(false)} style={{ color: '#0F6392', fontWeight: 500 }}>
            <LayoutGrid size={16} aria-hidden="true" />View the complete app library
          </a>
        </div>
      )}

      {account.open && account.position && (
        <div ref={account.panelRef} className="ws-popover ws-overlay" role="menu" aria-label="Account" style={{ ...account.position, width: 260 }}>
          <a role="menuitem" className="ws-menu-item" href={autoAppyUrl('/profile')}><CircleUserRound size={16} />Profile</a>
          <a role="menuitem" className="ws-menu-item" href={autoAppyUrl('/edit-profile')}><PencilLine size={16} />Edit profile</a>
          <button role="menuitem" type="button" className="ws-menu-item" onClick={signOut} style={{ color: '#AD3B3B' }}><LogOut size={16} color="#AD3B3B" />Log out</button>
        </div>
      )}
    </>
  );
}
